import jsonpatch from 'fast-json-patch';

import { COLLECTION_TYPES } from '@/core/collections/constants';
import type { InitiativeBarDefinition, InitiativeEntry, InitiativeState } from '@/initiative';
import { defaultInitiativeType, initiativeType } from '@/table/initiative/definition';
import { tokenLabel } from '@/table/initiative/tokenLabel';
import { tokenCharacter } from '@/characters/utils/tokenCharacter';

// TODO: type this — Vuex module context and collection records
/* eslint-disable @typescript-eslint/no-explicit-any */
type Ctx = any;
type Token = Record<string, any>;
/* eslint-enable @typescript-eslint/no-explicit-any */

interface PersistedEntry {
    id: string;
    spent: boolean;
}

/**
 * What the campaign's initiative document holds. Deliberately no labels,
 * portraits, or colours — those are read from the live token, so a renamed token
 * renames its chip.
 *
 * WARNING: `activeId` is '' rather than null when nobody is acting. The server's
 * JSON-patch translator throws ArgumentException on a null patch value, so a
 * null can be created but never patched in.
 */
export interface PersistedInitiative {
    entries: PersistedEntry[];
    activeId: string;
    round: number;
    definitionId: string;
}

type TurnOrder = Omit<PersistedInitiative, 'definitionId'>;

const NOBODY_ACTING = '';
const NO_COMBATANTS: TurnOrder = { entries: [], activeId: NOBODY_ACTING, round: 1 };
const NO_INITIATIVE: PersistedInitiative = { ...NO_COMBATANTS, definitionId: defaultInitiativeType.id };

function toViewEntry(
    entry: PersistedEntry,
    token: Token | null,
    lookupCharacter: (id: string) => Record<string, unknown> | null,
): InitiativeEntry | null {
    if (!token) return null;

    const character = tokenCharacter(token as Record<string, unknown>, lookupCharacter);
    return {
        id: entry.id,
        label: tokenLabel(character),
        color: (character?.borderColor as string) || undefined,
        spent: !!entry.spent,
    };
}

function toTurnOrder(state: InitiativeState): TurnOrder {
    return {
        entries: state.entries.map((entry) => ({ id: entry.id, spent: entry.spent })),
        activeId: state.activeId ?? NOBODY_ACTING,
        round: state.round,
    };
}

const InitiativeStore = {
    namespaced: true,
    state: () => ({
        collapsed: true,
        tokenDragging: false,
    }),
    getters: {
        // Two players dragging into an empty bar at once can each create a document.
        // Sorting means every client then agrees on which one is the turn order.
        document(_state: Ctx, _getters: Ctx, _rootState: Ctx, rootGetters: Ctx): Token | null {
            const collection = rootGetters['collections/getCollection'](COLLECTION_TYPES.initiative) ?? {};
            const [firstId] = Object.keys(collection).sort();
            return firstId ? collection[firstId] : null;
        },
        documentId(_state: Ctx, getters: Ctx): string | null {
            return getters.document?._id ?? null;
        },
        persisted(_state: Ctx, getters: Ctx): PersistedInitiative {
            const document = getters.document;
            if (!document) return NO_INITIATIVE;

            return {
                entries: Array.isArray(document.entries) ? document.entries : [],
                activeId: document.activeId ?? NOBODY_ACTING,
                round: document.round ?? 1,
                definitionId: document.definitionId ?? defaultInitiativeType.id,
            };
        },
        // Board tokens are embedded in their map document, not in a `tokens` collection,
        // so they are resolved through the tokens store rather than looked up by id.
        tokensById(_state: Ctx, _getters: Ctx, _rootState: Ctx, rootGetters: Ctx): Map<string, Token> {
            const tokens: Token[] = rootGetters['tokens/getTokens'] ?? [];
            return new Map(tokens.map((token) => [token._id, token]));
        },
        tokenFor: (_state: Ctx, getters: Ctx) => (tokenId: string) => {
            return getters.tokensById.get(tokenId) ?? null;
        },
        turnOrder(_state: Ctx, getters: Ctx, _rootState: Ctx, rootGetters: Ctx): InitiativeState {
            const { entries, activeId, round } = getters.persisted;
            const lookupCharacter = rootGetters['characters/getRawCharacter'];
            const resolved = entries
                .map((entry: PersistedEntry) => toViewEntry(entry, getters.tokenFor(entry.id), lookupCharacter))
                .filter((entry: InitiativeEntry | null): entry is InitiativeEntry => entry !== null);

            return { entries: resolved, activeId: activeId || null, round };
        },
        definition(_state: Ctx, getters: Ctx): InitiativeBarDefinition {
            return initiativeType(getters.persisted.definitionId);
        },
    },
    mutations: {
        setCollapsed(state: Ctx, collapsed: boolean): void {
            state.collapsed = collapsed;
        },
        setTokenDragging(state: Ctx, dragging: boolean): void {
            state.tokenDragging = dragging;
        },
    },
    actions: {
        async load({ dispatch }: Ctx): Promise<unknown> {
            return await dispatch('collections/load', { collection: COLLECTION_TYPES.initiative }, { root: true });
        },

        async apply({ dispatch }: Ctx, state: InitiativeState): Promise<unknown> {
            return await dispatch('write', toTurnOrder(state));
        },

        async addToken({ dispatch, getters }: Ctx, token: Token): Promise<unknown> {
            if (!token?._id) return null;

            const { entries, activeId, round } = getters.persisted;
            if (entries.some((entry: PersistedEntry) => entry.id === token._id)) return null;

            if (token.private) {
                dispatch(
                    'notifications/notify',
                    { message: 'A private token cannot be added to the turn order' },
                    { root: true },
                );
                return null;
            }

            return await dispatch('write', {
                entries: [...entries, { id: token._id, spent: false }],
                activeId: activeId || token._id,
                round,
            });
        },

        async chooseDefinition({ dispatch, getters }: Ctx, definitionId: string): Promise<unknown> {
            if (getters.persisted.definitionId === definitionId) return null;
            return await dispatch('write', { definitionId });
        },

        async clear({ dispatch, getters }: Ctx): Promise<unknown> {
            if (!getters.persisted.entries.length) return null;
            return await dispatch('write', NO_COMBATANTS);
        },

        // The bar reports turn order only, so anything else the document carries has
        // to survive a write rather than be diffed away.
        async write({ dispatch, getters }: Ctx, changes: Partial<PersistedInitiative>): Promise<unknown> {
            const current: PersistedInitiative = getters.persisted;
            const next: PersistedInitiative = { ...current, ...changes };

            if (!getters.document) {
                return await dispatch(
                    'collections/add',
                    { collection: COLLECTION_TYPES.initiative, item: next },
                    { root: true },
                );
            }

            const patch = jsonpatch.compare(current, next);
            if (!patch.length) return null;

            return await dispatch(
                'collections/update',
                { collection: COLLECTION_TYPES.initiative, id: getters.documentId, patch },
                { root: true },
            );
        },
    },
};

export default InitiativeStore;
